/**
 * DH Commerce Personalization Engine
 * Framework baseline: v1.4
 *
 * Builds and persists a lightweight shopper profile using:
 * - declared preferences
 * - browsing behavior
 * - category and product affinity
 * - dietary interests
 * - campaign source
 * - visit history
 *
 * This module is intentionally safe by default:
 * it records and exposes profile data, but does not change the storefront
 * until another module or configuration explicitly uses that data.
 *
 * Requires:
 *   dh-commerce-rules.js
 *
 * Recommended load order:
 *   dh-commerce-rules.js
 *   dh-commerce-offers.js
 *   dh-commerce-personalization.js
 */

(function (window, document) {
  "use strict";

  var DHCommerce = window.DHCommerce = window.DHCommerce || {};

  if (!DHCommerce.ruleEngine) {
    console.warn(
      "DH Commerce Personalization: dh-commerce-rules.js must load before dh-commerce-personalization.js."
    );
    return;
  }

  var STORAGE_KEY = "dhcommerce_personalization_v1_4";
  var SESSION_KEY = "dhcommerce_session_v1_4";
  var MAX_RECENT_ITEMS = 20;
  var DEFAULT_AFFINITY_STEP = 10;

  DHCommerce.state = DHCommerce.state || {};
  DHCommerce.personalization = DHCommerce.personalization || {};
  DHCommerce.personalizationEngine =
    DHCommerce.personalizationEngine || {};

  function nowIso() {
    return new Date().toISOString();
  }

  function asArray(value) {
    if (value == null) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }

  function clone(value) {
    if (value == null) {
      return value;
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return value;
    }
  }

  function merge(target, source) {
    var output = target && typeof target === "object"
      ? clone(target)
      : {};

    if (!source || typeof source !== "object") {
      return output;
    }

    Object.keys(source).forEach(function (key) {
      var sourceValue = source[key];
      var targetValue = output[key];

      if (
        sourceValue &&
        typeof sourceValue === "object" &&
        !Array.isArray(sourceValue)
      ) {
        output[key] = merge(
          targetValue && typeof targetValue === "object"
            ? targetValue
            : {},
          sourceValue
        );
      } else {
        output[key] = clone(sourceValue);
      }
    });

    return output;
  }

  function safeParse(value, fallback) {
    if (!value) {
      return fallback;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function readLocal() {
    try {
      return safeParse(
        window.localStorage.getItem(STORAGE_KEY),
        {}
      );
    } catch (error) {
      return {};
    }
  }

  function writeLocal(profile) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(profile)
      );
      return true;
    } catch (error) {
      console.warn(
        "DH Commerce Personalization: Unable to persist profile.",
        error
      );
      return false;
    }
  }

  function readSession() {
    try {
      return safeParse(
        window.sessionStorage.getItem(SESSION_KEY),
        {}
      );
    } catch (error) {
      return {};
    }
  }

  function writeSession(session) {
    try {
      window.sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify(session)
      );
      return true;
    } catch (error) {
      return false;
    }
  }

  function normalizeKey(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");
  }

  function uniquePush(list, item, maxItems) {
    var normalizedList = asArray(list).filter(Boolean);
    var serialized = JSON.stringify(item);

    normalizedList = normalizedList.filter(function (existing) {
      return JSON.stringify(existing) !== serialized;
    });

    normalizedList.unshift(item);

    return normalizedList.slice(
      0,
      maxItems || MAX_RECENT_ITEMS
    );
  }

  function dispatch(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: detail || {}
      })
    );
  }

  function createDefaultProfile() {
    return {
      version: "1.4",
      visitorId: null,
      firstSeenAt: nowIso(),
      lastSeenAt: nowIso(),
      visits: 0,
      source: {
        first: null,
        latest: null
      },
      declared: {
        dietary: {},
        categories: {},
        flavors: {}
      },
      affinity: {
        categories: {},
        products: {},
        flavors: {},
        dietary: {}
      },
      behavior: {
        pageViews: 0,
        productViews: 0,
        categoryViews: 0,
        assortmentStarts: 0,
        addToCarts: 0,
        purchases: 0,
        averageOrderValue: 0,
        lifetimeValue: 0
      },
      recent: {
        products: [],
        categories: [],
        searches: []
      },
      flags: {
        returningVisitor: false,
        highIntent: false,
        repeatCustomer: false
      },
      metadata: {}
    };
  }

  function createVisitorId() {
    return [
      "dh",
      Date.now().toString(36),
      Math.random().toString(36).slice(2, 10)
    ].join("-");
  }

  function captureSource() {
    var params = new URLSearchParams(
      window.location.search
    );

    var source = {
      referrer: document.referrer || null,
      utmSource: params.get("utm_source"),
      utmMedium: params.get("utm_medium"),
      utmCampaign: params.get("utm_campaign"),
      utmContent: params.get("utm_content"),
      utmTerm: params.get("utm_term"),
      querySource:
        params.get("source") ||
        params.get("src") ||
        null,
      landingPath:
        window.location.pathname +
        window.location.search
    };

    var hasValue = Object.keys(source).some(function (key) {
      return Boolean(source[key]);
    });

    return hasValue ? source : null;
  }

  function calculateFlags(profile) {
    profile.flags = profile.flags || {};
    profile.flags.returningVisitor =
      Number(profile.visits || 0) > 1;

    profile.flags.repeatCustomer =
      Number(profile.behavior.purchases || 0) > 1;

    profile.flags.highIntent =
      Number(profile.behavior.addToCarts || 0) > 0 ||
      Number(profile.behavior.productViews || 0) >= 3 ||
      Number(profile.behavior.assortmentStarts || 0) > 0;

    return profile;
  }

  function hydrateState(profile) {
    DHCommerce.personalization.profile = profile;
    DHCommerce.state.personalization = clone(profile);

    DHCommerce.state.customer =
      DHCommerce.state.customer || {};

    DHCommerce.state.customer.affinity =
      clone(profile.affinity || {});

    DHCommerce.state.customer.preferences =
      clone(profile.declared || {});

    DHCommerce.state.customer.behavior =
      clone(profile.behavior || {});

    DHCommerce.state.customer.flags =
      clone(profile.flags || {});
  }

  function persist(profile, reason) {
    profile.lastSeenAt = nowIso();
    calculateFlags(profile);
    writeLocal(profile);
    hydrateState(profile);

    dispatch(
      "dhcommerce:personalization-updated",
      {
        profile: clone(profile),
        reason: reason || "update"
      }
    );

    dispatch(
      "dhcommerce:state-changed",
      {
        state: DHCommerce.state,
        source: "personalization",
        reason: reason || "update"
      }
    );

    return profile;
  }

  function initializeProfile() {
    var stored = readLocal();
    var profile = merge(
      createDefaultProfile(),
      stored
    );

    if (!profile.visitorId) {
      profile.visitorId = createVisitorId();
    }

    var session = readSession();

    if (!session.startedAt) {
      session.startedAt = nowIso();
      session.countedVisit = false;
    }

    if (!session.countedVisit) {
      profile.visits =
        Number(profile.visits || 0) + 1;
      session.countedVisit = true;
      writeSession(session);
    }

    var source = captureSource();

    if (source) {
      if (!profile.source.first) {
        profile.source.first = clone(source);
      }

      profile.source.latest = clone(source);
    }

    profile.behavior.pageViews =
      Number(profile.behavior.pageViews || 0) + 1;

    return persist(profile, "initialize");
  }

  function incrementAffinity(bucket, key, amount) {
    var profile = DHCommerce.personalization.profile;
    var normalizedBucket = normalizeKey(bucket);
    var normalizedKey = normalizeKey(key);

    if (!profile || !normalizedBucket || !normalizedKey) {
      return profile;
    }

    profile.affinity[normalizedBucket] =
      profile.affinity[normalizedBucket] || {};

    profile.affinity[normalizedBucket][normalizedKey] =
      Number(
        profile.affinity[normalizedBucket][normalizedKey] || 0
      ) +
      Number(amount || DEFAULT_AFFINITY_STEP);

    return persist(
      profile,
      "affinity:" +
        normalizedBucket +
        ":" +
        normalizedKey
    );
  }

  function setDeclaredPreference(group, key, value) {
    var profile = DHCommerce.personalization.profile;
    var normalizedGroup = normalizeKey(group);
    var normalizedKey = normalizeKey(key);

    if (!profile || !normalizedGroup || !normalizedKey) {
      return profile;
    }

    profile.declared[normalizedGroup] =
      profile.declared[normalizedGroup] || {};

    profile.declared[normalizedGroup][normalizedKey] =
      value === undefined ? true : value;

    return persist(
      profile,
      "preference:" +
        normalizedGroup +
        ":" +
        normalizedKey
    );
  }

  function recordProductView(product) {
    var profile = DHCommerce.personalization.profile;

    if (!profile || !product) {
      return profile;
    }

    var item = {
      id:
        product.id ||
        product.productId ||
        product.product_id ||
        null,
      handle:
        product.handle ||
        product.productHandle ||
        null,
      title: product.title || null,
      category:
        product.category ||
        product.productType ||
        product.product_type ||
        null,
      flavor: product.flavor || null,
      viewedAt: nowIso()
    };

    profile.behavior.productViews =
      Number(profile.behavior.productViews || 0) + 1;

    profile.recent.products = uniquePush(
      profile.recent.products,
      item,
      MAX_RECENT_ITEMS
    );

    if (item.handle || item.id) {
      incrementAffinity(
        "products",
        item.handle || item.id,
        product.affinityWeight || DEFAULT_AFFINITY_STEP
      );
      profile = DHCommerce.personalization.profile;
    }

    if (item.category) {
      incrementAffinity(
        "categories",
        item.category,
        product.categoryWeight || 6
      );
      profile = DHCommerce.personalization.profile;
    }

    if (item.flavor) {
      incrementAffinity(
        "flavors",
        item.flavor,
        product.flavorWeight || 4
      );
      profile = DHCommerce.personalization.profile;
    }

    return persist(profile, "product-view");
  }

  function recordCategoryView(category) {
    var profile = DHCommerce.personalization.profile;
    var key = normalizeKey(category);

    if (!profile || !key) {
      return profile;
    }

    profile.behavior.categoryViews =
      Number(profile.behavior.categoryViews || 0) + 1;

    profile.recent.categories = uniquePush(
      profile.recent.categories,
      {
        category: key,
        viewedAt: nowIso()
      },
      MAX_RECENT_ITEMS
    );

    incrementAffinity(
      "categories",
      key,
      DEFAULT_AFFINITY_STEP
    );

    return persist(
      DHCommerce.personalization.profile,
      "category-view"
    );
  }

  function recordSearch(query) {
    var profile = DHCommerce.personalization.profile;
    var value = String(query || "").trim();

    if (!profile || !value) {
      return profile;
    }

    profile.recent.searches = uniquePush(
      profile.recent.searches,
      {
        query: value,
        searchedAt: nowIso()
      },
      MAX_RECENT_ITEMS
    );

    return persist(profile, "search");
  }

  function recordAddToCart(item) {
    var profile = DHCommerce.personalization.profile;

    if (!profile) {
      return profile;
    }

    profile.behavior.addToCarts =
      Number(profile.behavior.addToCarts || 0) + 1;

    if (item && item.category) {
      incrementAffinity(
        "categories",
        item.category,
        item.affinityWeight || 15
      );
      profile = DHCommerce.personalization.profile;
    }

    if (item && item.handle) {
      incrementAffinity(
        "products",
        item.handle,
        item.productWeight || 20
      );
      profile = DHCommerce.personalization.profile;
    }

    return persist(profile, "add-to-cart");
  }

  function recordPurchase(order) {
    var profile = DHCommerce.personalization.profile;

    if (!profile) {
      return profile;
    }

    var orderValue = Number(
      order &&
      (
        order.total ||
        order.totalPrice ||
        order.total_price
      )
    ) || 0;

    var previousPurchases =
      Number(profile.behavior.purchases || 0);

    var previousLifetime =
      Number(profile.behavior.lifetimeValue || 0);

    var purchaseCount = previousPurchases + 1;
    var lifetimeValue = previousLifetime + orderValue;

    profile.behavior.purchases = purchaseCount;
    profile.behavior.lifetimeValue = lifetimeValue;
    profile.behavior.averageOrderValue =
      purchaseCount > 0
        ? lifetimeValue / purchaseCount
        : 0;

    asArray(order && order.items).forEach(function (item) {
      if (item.category) {
        incrementAffinity(
          "categories",
          item.category,
          30
        );
      }

      if (item.handle) {
        incrementAffinity(
          "products",
          item.handle,
          40
        );
      }

      if (item.flavor) {
        incrementAffinity(
          "flavors",
          item.flavor,
          20
        );
      }
    });

    return persist(
      DHCommerce.personalization.profile,
      "purchase"
    );
  }

  function getTopAffinity(bucket, limit) {
    var profile = DHCommerce.personalization.profile;
    var values =
      profile &&
      profile.affinity &&
      profile.affinity[normalizeKey(bucket)]
        ? profile.affinity[normalizeKey(bucket)]
        : {};

    return Object.keys(values)
      .map(function (key) {
        return {
          key: key,
          score: Number(values[key] || 0)
        };
      })
      .sort(function (a, b) {
        return b.score - a.score;
      })
      .slice(0, Number(limit || 5));
  }

  DHCommerce.conditions.personalizationFlag =
    function (condition, context) {
      var state = context || DHCommerce.state || {};
      var flags =
        state.personalization &&
        state.personalization.flags
          ? state.personalization.flags
          : {};

      var actual = flags[condition.name];

      return condition.value === undefined
        ? Boolean(actual)
        : actual === condition.value;
    };

  DHCommerce.conditions.affinityScore =
    function (condition, context) {
      var state = context || DHCommerce.state || {};
      var profile =
        state.personalization ||
        DHCommerce.personalization.profile ||
        {};

      var bucket = normalizeKey(condition.bucket);
      var key = normalizeKey(condition.key);
      var score =
        profile.affinity &&
        profile.affinity[bucket]
          ? Number(
              profile.affinity[bucket][key] || 0
            )
          : 0;

      var expected = Number(condition.value || 0);
      var operator = condition.operator || "gte";

      switch (operator) {
        case "gt":
          return score > expected;
        case "gte":
          return score >= expected;
        case "lt":
          return score < expected;
        case "lte":
          return score <= expected;
        case "eq":
          return score === expected;
        case "neq":
          return score !== expected;
        default:
          return false;
      }
    };

  DHCommerce.conditions.declaredPreference =
    function (condition, context) {
      var state = context || DHCommerce.state || {};
      var profile =
        state.personalization ||
        DHCommerce.personalization.profile ||
        {};

      var group = normalizeKey(condition.group);
      var key = normalizeKey(condition.key);
      var actual =
        profile.declared &&
        profile.declared[group]
          ? profile.declared[group][key]
          : undefined;

      return condition.value === undefined
        ? Boolean(actual)
        : actual === condition.value;
    };

  DHCommerce.personalizationEngine = {
    initialize: function () {
      return initializeProfile();
    },

    getProfile: function () {
      return clone(
        DHCommerce.personalization.profile
      );
    },

    updateProfile: function (updates, reason) {
      var current =
        DHCommerce.personalization.profile ||
        createDefaultProfile();

      var profile = merge(current, updates || {});

      return persist(
        profile,
        reason || "manual-update"
      );
    },

    setPreference: function (
      group,
      key,
      value
    ) {
      return setDeclaredPreference(
        group,
        key,
        value
      );
    },

    addAffinity: function (
      bucket,
      key,
      amount
    ) {
      return incrementAffinity(
        bucket,
        key,
        amount
      );
    },

    getTopAffinity: function (
      bucket,
      limit
    ) {
      return getTopAffinity(
        bucket,
        limit
      );
    },

    recordProductView: function (product) {
      return recordProductView(product);
    },

    recordCategoryView: function (category) {
      return recordCategoryView(category);
    },

    recordSearch: function (query) {
      return recordSearch(query);
    },

    recordAddToCart: function (item) {
      return recordAddToCart(item);
    },

    recordAssortmentStart: function () {
      var profile =
        DHCommerce.personalization.profile;

      if (!profile) {
        return profile;
      }

      profile.behavior.assortmentStarts =
        Number(
          profile.behavior.assortmentStarts || 0
        ) + 1;

      return persist(
        profile,
        "assortment-start"
      );
    },

    recordPurchase: function (order) {
      return recordPurchase(order);
    },

    reset: function () {
      try {
        window.localStorage.removeItem(
          STORAGE_KEY
        );
        window.sessionStorage.removeItem(
          SESSION_KEY
        );
      } catch (error) {
        // Ignore storage cleanup failures.
      }

      var profile = initializeProfile();

      dispatch(
        "dhcommerce:personalization-reset",
        {
          profile: clone(profile)
        }
      );

      return profile;
    }
  };

  document.addEventListener(
    "dhcommerce:product-viewed",
    function (event) {
      DHCommerce.personalizationEngine
        .recordProductView(
          event.detail || {}
        );
    }
  );

  document.addEventListener(
    "dhcommerce:category-viewed",
    function (event) {
      var category =
        event.detail &&
        (
          event.detail.category ||
          event.detail.value
        );

      DHCommerce.personalizationEngine
        .recordCategoryView(category);
    }
  );

  document.addEventListener(
    "dhcommerce:search",
    function (event) {
      var query =
        event.detail &&
        (
          event.detail.query ||
          event.detail.value
        );

      DHCommerce.personalizationEngine
        .recordSearch(query);
    }
  );

  document.addEventListener(
    "dhcommerce:add-to-cart",
    function (event) {
      DHCommerce.personalizationEngine
        .recordAddToCart(
          event.detail || {}
        );
    }
  );

  document.addEventListener(
    "dhcommerce:assortment-started",
    function () {
      DHCommerce.personalizationEngine
        .recordAssortmentStart();
    }
  );

  document.addEventListener(
    "dhcommerce:purchase",
    function (event) {
      DHCommerce.personalizationEngine
        .recordPurchase(
          event.detail || {}
        );
    }
  );

  var initializedProfile =
    DHCommerce.personalizationEngine.initialize();

  document.dispatchEvent(
    new CustomEvent(
      "dhcommerce:personalization-ready",
      {
        detail: {
          version: "1.4",
          profile: clone(initializedProfile)
        }
      }
    )
  );

})(window, document);
